import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { Box, NotFound } from '@factorialco/deprecated-design-system'
import { OrgChartPageDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
// nosemgrep do_not_use_core_card
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import {
  isOrphan,
  peopleHierarchicallyManagedByEmployee,
} from 'modules/employees/store/collections/employeesContractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import OrgChart from 'modules/org_chart/components/OrgChart'
import Provider from 'modules/org_chart/components/Provider'
import CollectionsBootstrap from 'modules/org_chart/lib/CollectionsBootstrap'
import TeamChartActivationBanner from 'modules/teams/components/TeamChartActivationBanner'

const Container = () => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(OrgChartPageDocument, { variables: { companyId: company.id } })
  const containerRef = React.useRef<HTMLDivElement>(null)

  const svgRef = React.useRef<SVGSVGElement>(null)
  const [size, setSize] = React.useState<[height: number, width: number]>([0, 0])

  const heads = useMemo<Employee[]>(
    () => employees.active.filter((x) => !x.attributes.manager_id || isOrphan(x)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  )

  const traceableEmployees = useUnsafeMemo<Employee[]>(
    () =>
      heads.reduce<Employee[]>(
        (acc, x) => [...acc, ...peopleHierarchicallyManagedByEmployee(x, contractVersions)],
        heads
      ),
    [heads]
  )

  const canSeeOrgChart = Boolean(data?.apiCore?._policies.accessReadOrgChart.can)
  const canCreateTeams = Boolean(data?.teams?._policies.teamCreate.can)
  const canSeeLegalGender = Boolean(data?.employees?._policies.seeEmployeeLegalGender.can)
  const canExportOrgChart = Boolean(data?.employees?._policies.exportOrgChart.can)

  const canExport =
    canExportOrgChart &&
    (data?.features?.featuresConnection.nodes.some(
      (el) => el.name === 'organization_chart_exports' && el.enabled
    ) ??
      false)

  const canSnapshot =
    data?.features?.featuresConnection.nodes.some(
      (el) => el.name === 'dev_orgchart_snapshot' && el.enabled
    ) ?? false

  const isReady = !loading && size[1] > 0

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current
      setSize((prevSize) => {
        // Update if size changes
        if (prevSize[0] !== offsetWidth || prevSize[1] !== offsetHeight) {
          return [offsetWidth, offsetHeight]
        }
        return prevSize
      })
    }
  }, [])

  useUnsafeEffect(() => {
    window.addEventListener('resize', handleResize)
    track(trackingEvents.employee.page.orgChart.visited)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useUnsafeEffect(() => handleResize())

  if (isReady && !canSeeOrgChart) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  return (
    <ResponsivePage.Body variant="fullscreen" showHeader={false} error={null} loading={loading}>
      <Box padding="s12" height="full">
        {isReady && canCreateTeams && <TeamChartActivationBanner companyId={company.id} />}

        <Provider>
          <Box width="full" height="full" ref={containerRef}>
            {isReady && (
              <OrgChart
                heads={heads}
                employees={traceableEmployees}
                size={size}
                ref={svgRef}
                showLegalGenders={canSeeLegalGender}
                canExport={canExport}
                canSnapshot={canSnapshot}
                contractVersions={contractVersions}
              />
            )}
            <Outlet />
          </Box>
        </Provider>
      </Box>
    </ResponsivePage.Body>
  )
}

const OrgChartPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}
export default OrgChartPage
